import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  notification,
} from "antd";
import useFetchData from "shared/hooks/useFetchData";
import axios from "axios";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import GiftCardForm from "./GiftCardForm";
import { PackageGroup } from "shared/constants/packageOptions";
import "./GiftCard.css";

import { API_BASE_URL } from "shared/api/config";

type GiftCard = {
  _id: string;
  package?: string;
  instagram?: string;
  code?: string;
  phone?: string | number;
  comephone?: string | number;
  createdAt?: string;
  updatedAt?: string;
};

interface ApiResponse {
  data: GiftCard[];
  success: string;
  message: string;
}

const legacyGiftPackages = [
  "60м Энгийн",
  "30м Энгийн",
  "60м Энгийн Жирэмсний",
  "60м Хөрөг",
  "30м Хөрөг",
  "60м Хөрөг Жирэмсний",
];

const normalizePackageGroups = (raw: any): PackageGroup[] => {
  const packageGroups = Array.isArray(raw) ? raw : raw?.data || [];
  const seen = new Set<string>();
  const groups: PackageGroup[] = [];

  const legacyOptions = legacyGiftPackages.map((name) => ({
    label: name,
    value: name,
    price: 0,
  }));
  legacyOptions.forEach((option) => seen.add(option.value));
  groups.push({ label: "Gift card хуучин нэршил", options: legacyOptions });

  packageGroups.forEach((group: PackageGroup) => {
    const options = (group.options || []).filter((option) => {
      if (!option.value || seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });

    if (options.length) {
      groups.push({ label: group.label, options });
    }
  });

  return groups;
};

const normalizePhone = (value?: string | number) => String(value || "").trim();

const GiftCardList: React.FC = () => {
  const { data, loading, error, refetch } = useFetchData<ApiResponse>(
    `${API_BASE_URL}/giftcard/get`
  );

  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<GiftCard | null>(null);
  const [packageGroups, setPackageGroups] = useState<PackageGroup[]>([]);
  const [search, setSearch] = useState("");
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/package/get`);
        setPackageGroups(normalizePackageGroups(res.data));
      } catch (err) {
        setPackageGroups(normalizePackageGroups([]));
      }
    };

    fetchPackages();
  }, []);

  const allRows = useMemo(() => data?.data || [], [data]);
  const packageOptions = useMemo(
    () =>
      Array.from(new Set(allRows.map((item) => item.package).filter(Boolean))).map(
        (name) => ({ label: name as string, value: name as string })
      ),
    [allRows]
  );

  const filteredData = useMemo(() => {
    const query = search.trim().toLowerCase();

    return allRows.filter((item) => {
      const matchesPackage = selectedPackage ? item.package === selectedPackage : true;
      const matchesSearch = query
        ? [item.code, item.instagram, item.phone, item.comephone, item.package]
            .map((value) => String(value || "").toLowerCase())
            .some((value) => value.includes(query))
        : true;

      return matchesPackage && matchesSearch;
    });
  }, [allRows, search, selectedPackage]);

  const openCreate = () => {
    setCurrentRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const openEdit = (record: GiftCard) => {
    setCurrentRecord(record);
    form.setFieldsValue({
      ...record,
      phone: normalizePhone(record.phone),
      comephone: normalizePhone(record.comephone),
    });
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setCurrentRecord(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        phone: normalizePhone(values.phone),
        comephone: normalizePhone(values.comephone),
      };

      if (currentRecord) {
        await axios.put(`${API_BASE_URL}/giftcard/update/${currentRecord._id}`, payload);
        notification.success({ message: "Gift card шинэчлэгдлээ" });
      } else {
        await axios.post(`${API_BASE_URL}/giftcard/create`, payload);
        notification.success({ message: "Gift card нэмэгдлээ" });
      }

      closeModal();
      refetch();
    } catch (err: any) {
      if (!err.errorFields) {
        notification.error({
          message: "Алдаа",
          description: err.response?.data?.message || "Gift card хадгалах үед алдаа гарлаа",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (record: GiftCard) => {
    try {
      await axios.delete(`${API_BASE_URL}/giftcard/delete/${record._id}`);
      notification.success({ message: "Gift card устгагдлаа" });
      refetch();
    } catch (err: any) {
      notification.error({
        message: "Алдаа",
        description: err.response?.data?.message || "Gift card устгахад алдаа гарлаа",
      });
    }
  };

  if (loading) {
    return (
      <div className="centered-state">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="Gift card дата татахад алдаа гарлаа" description={error.message} type="error" />;
  }

  return (
    <div className="gift-page">
      <Card className="gift-toolbar">
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <Input.Search
              allowClear
              placeholder="Код, утас, instagram хайх"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Багцаар шүүх"
              value={selectedPackage}
              onChange={setSelectedPackage}
              options={packageOptions}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Gift card нэмэх
            </Button>
          </Col>
        </Row>
      </Card>

      <Table<GiftCard>
        dataSource={filteredData}
        rowKey="_id"
        size="small"
        bordered
        scroll={{ x: 900 }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        columns={[
          {
            title: "Үйлдэл",
            key: "actions",
            fixed: "left",
            width: 110,
            render: (_, record) => (
              <Space>
                <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
                <Popconfirm
                  title="Gift card устгах уу?"
                  okText="Устгах"
                  cancelText="Болих"
                  onConfirm={() => handleDelete(record)}
                >
                  <Button danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
              </Space>
            ),
          },
          {
            title: "Багц",
            dataIndex: "package",
            key: "package",
            render: (value) => <Tag color="cyan">{value || "-"}</Tag>,
          },
          { title: "Код", dataIndex: "code", key: "code" },
          { title: "Instagram", dataIndex: "instagram", key: "instagram" },
          {
            title: "Авсан хүний утас",
            dataIndex: "phone",
            key: "phone",
            render: normalizePhone,
          },
          {
            title: "Ирэх хүний утас",
            dataIndex: "comephone",
            key: "comephone",
            render: normalizePhone,
          },
          {
            title: "Үүссэн",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value?: string) => (value ? new Date(value).toLocaleDateString() : "-"),
          },
        ]}
      />

      <Modal
        title={currentRecord ? "Gift card засах" : "Gift card нэмэх"}
        open={isModalVisible}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={isSubmitting}
        okText="Хадгалах"
        cancelText="Болих"
      >
        <GiftCardForm form={form} packageGroups={packageGroups} />
      </Modal>
    </div>
  );
};

export default GiftCardList;
